import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { loginUserWithGoogle } from '../userSlice';
import { closeAccountModal, selectAccountModal } from '../accountModalSlice';
import { useNavigate, useParams } from 'react-router-dom';
import { useRestaurant } from '@/features/restaurant/useRestaurant';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (cfg: {
            client_id: string;
            callback: (resp: { credential: string }) => void;
          }) => void;
          renderButton: (parent: HTMLElement, opts: Record<string, unknown>) => void;
        };
      };
    };
  }
}

/**
 * Whether social (Google) login can be offered for this deployment. False when
 * no OAuth client id is configured (e.g. preprod) — callers hide the
 * "or continue with" separator so it is not left orphaned above an empty area.
 */
export const isGoogleLoginEnabled = () => Boolean(import.meta.env.VITE_GOOGLE_CLIENT_ID);

// SC-777: index.html loads the GSI script with `async defer`. The render effect
// used to look for `window.google` exactly once — when the script landed after
// the mount (slow network, account modal opened straight away) the button never
// appeared and nothing said why. Wait for it, then give up visibly.
const GSI_POLL_MS = 200;
const GSI_TIMEOUT_MS = 10000;

export default function GoogleButton() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { redirectTo } = useAppSelector(selectAccountModal);
  const { restaurantUuid = '' } = useParams<{ restaurantUuid: string }>();
  const restaurant = useRestaurant(restaurantUuid);
  const ruuid = restaurant?.identity?.uuid;
  const buttonRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [gsiReady, setGsiReady] = useState(() => Boolean(window.google));
  const [gsiUnavailable, setGsiUnavailable] = useState(false);

  useEffect(() => {
    if (gsiReady || gsiUnavailable) return;
    let waited = 0;
    const timer = setInterval(() => {
      if (window.google) {
        clearInterval(timer);
        setGsiReady(true);
      } else {
        waited += GSI_POLL_MS;
        if (waited >= GSI_TIMEOUT_MS) {
          clearInterval(timer);
          setGsiUnavailable(true);
        }
      }
    }, GSI_POLL_MS);
    return () => clearInterval(timer);
  }, [gsiReady, gsiUnavailable]);

  useEffect(() => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId || !ruuid || !buttonRef.current || !gsiReady || !window.google) return;

    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: async (resp) => {
        setError(null);
        const result = await dispatch(
          loginUserWithGoogle({ id_token: resp.credential, restaurant_uuid: ruuid }),
        );
        if (loginUserWithGoogle.fulfilled.match(result)) {
          dispatch(closeAccountModal());
          if (redirectTo) navigate(redirectTo);
        } else {
          const code = result.payload as string;
          setError(
            code === 'google_email_not_verified'
              ? t('auth.errors.googleNotVerified')
              : t('auth.errors.googleFailed'),
          );
        }
      },
    });

    window.google.accounts.id.renderButton(buttonRef.current, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'continue_with',
      shape: 'rectangular',
      logo_alignment: 'left',
      width: 320,
    });
  }, [dispatch, navigate, redirectTo, ruuid, gsiReady]);

  return (
    <div className="w-full">
      <div ref={buttonRef} className="w-full flex justify-center" />
      {gsiUnavailable && (
        <p className="text-sm text-ink/70">{t('auth.errors.googleUnavailable')}</p>
      )}
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
}
